import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { SessionReplayOptions } from '@amplitude/plugin-session-replay-browser/lib/esm/typings/session-replay';
import { MessageType, SyncUserSchoolRelation } from '@schooly/api';

export const DEFAULT_SESSION_REPLAY_SAMPLE_RATE = 0.1;
export const INCREASED_SESSION_REPLAY_SAMPLE_RATE = 0.5;

export enum AnalyticsEventName {
  // student events
  StudentSelect = 'select_student',

  // markbook events
  MarkbookOpen = 'open_markbook',

  // report events
  ReportOpen = 'open_report',
  ReportStatusChange = 'change_report_status',
  ReportListOpen = 'open_reports_module',
  ReportCommentsVisibilityToggle = 'toggle_comments_visibility',
  ReportChangeArrangementType = 'change_arrangement_type',
  ReportEditScoreGrade = 'edit_score_grade',

  // assessment events
  AssessmentListOpen = 'open_assessments_module',
  AssessmentStatusChange = 'change_assessment_status',
  AssessmentAoLChange = 'edit_area_of_learning',
  AssessmentChangeStudentArrangement = 'change_student_assessment_arrangement',
  AssessmentEditStudentAssessments = 'edit_student_assessments',
  AssessmentOpenComment = 'open_comment_popup',
  AssessmentEditComment = 'edit_comment',

  // attendance events
  AttendanceRegisterListView = 'attendance_register_list_view',
  AttendanceRegisterFilterApplied = 'attendance_register_filter_applied',
  AttendanceRegisterExportCSV = 'attendance_register_export_csv',
  AttendanceGroupView = 'attendance_group_view',
  AttendanceMarkStatus = 'attendance_mark_status',
  AttendanceSave = 'attendance_save',
  AttendanceSettingsView = 'attendance_settings_view',
  AttendanceCodeSave = 'attendance_code_save',
  AttendanceNotificationsToggle = 'attendance_notifications_toggle',
  AttendanceJustificationsListView = 'absence_justifications_list_view',
  AttendanceJustificationsSearch = 'absence_justifications_search',
  AttendanceJustificationView = 'absence_justification_view',
  AttendanceJustificationClosed = 'absence_justification_closed',
  AttendanceJustificationReopen = 'absence_justification_reopen',

  // annual planner events
  DrillDownAnnuallySelect = 'DrillDown_annually_select',
  DrillDownTermlySelect = 'DrillDown_termly_select',
  DrillDownMonthlySelect = 'DrillDown_monthly_select',
  DrillDownAnnuallyShown = 'DrillDown_annually',
  DrillDownTermlyShown = 'DrillDown_termly',
  DrillDownMonthlyShown = 'DrillDown_monthly',

  //leaving reason events
  RLOpen = 'RL_open',
  RLAddReason = 'RL_add_reason',
  RLArchiveReason = 'RL_archive_reason',

  // notification center events
  NotificationsOpenWeb = 'notifications_open_web',
  ClickNotification = 'click_notification',
  ClearMessageNotifications = 'clear_message_notifications',

  // thread events
  StartFamilyThread = 'start_family_thread',
  ViewThreadsTab = 'view_threads_tab',
  SendThreadMessage = 'send_thread_message',
  PublisherJoinsThread = 'publisher_joins_thread',
  DeleteThreadMessage = 'delete_thread_message',

  // message events
  SelectMessageType = 'select_message_type',
  PublishMessage = 'publish_message',

  // students bulk action events
  StudentsBulkMessageParents = 'students_bulk_message_parents_initiated',

  // trial request events
  TrialRequestSubmission = 'trial_request_submission',

  // invite events
  InviteAccept = 'invite_accept',
}

type AnalyticsEvent<T extends AnalyticsEventName, P = undefined> = {
  name: T;
  payload?: P;
};

export type AnnualPlannerViewAnalyticsEvent = AnalyticsEvent<
  | AnalyticsEventName.DrillDownAnnuallySelect
  | AnalyticsEventName.DrillDownTermlySelect
  | AnalyticsEventName.DrillDownMonthlySelect
  | AnalyticsEventName.DrillDownAnnuallyShown
  | AnalyticsEventName.DrillDownTermlyShown
  | AnalyticsEventName.DrillDownMonthlyShown
>;

type MessageAnalyticsEvent = AnalyticsEvent<
  AnalyticsEventName.SelectMessageType | AnalyticsEventName.PublishMessage,
  { type: MessageType }
>;

type NotificationCenterAnalyticsEvent = AnalyticsEvent<
  | AnalyticsEventName.NotificationsOpenWeb
  | AnalyticsEventName.ClickNotification
  | AnalyticsEventName.ClearMessageNotifications,
  {
    message_id?: string;
    thread_id?: string;
    cleared_at?: string;
  }
>;

type ThreadAnalyticsEvent = AnalyticsEvent<
  | AnalyticsEventName.StartFamilyThread
  | AnalyticsEventName.ViewThreadsTab
  | AnalyticsEventName.SendThreadMessage
  | AnalyticsEventName.PublisherJoinsThread
  | AnalyticsEventName.DeleteThreadMessage,
  {
    thread_id?: string;
    message_id?: string;
    started_at?: string;
    user_id?: string;
    deleted_at?: string;
    message_length?: number;
  }
>;

type ReportAnalyticsEvent = AnalyticsEvent<
  | AnalyticsEventName.ReportOpen
  | AnalyticsEventName.ReportStatusChange
  | AnalyticsEventName.ReportListOpen
  | AnalyticsEventName.ReportCommentsVisibilityToggle
  | AnalyticsEventName.ReportChangeArrangementType
  | AnalyticsEventName.ReportEditScoreGrade,
  {
    report_name?: string;
    report_date?: string;
    status?: string;
    new_status?: string;
    action?: string;
    aol?: string[];
    subject_name?: string;
    new_value?: string;
    previous_value?: string;
    arrangement_type?: 'subject' | 'area_of_learning';
  }
>;

type StudentAnalyticsEvent = AnalyticsEvent<
  AnalyticsEventName.StudentSelect,
  { student_id?: string; student_name?: string }
>;

type AssessmentAnalyticsEvent = AnalyticsEvent<
  | AnalyticsEventName.AssessmentListOpen
  | AnalyticsEventName.AssessmentStatusChange
  | AnalyticsEventName.AssessmentAoLChange
  | AnalyticsEventName.AssessmentEditStudentAssessments
  | AnalyticsEventName.AssessmentChangeStudentArrangement
  | AnalyticsEventName.AssessmentOpenComment
  | AnalyticsEventName.AssessmentEditComment,
  {
    assessment_id?: string;
    new_status?: string;
    area_of_learning_name?: string;
    action?: 'add' | 'edit';
    arrangement_type?: 'subject' | 'area_of_learning' | 'date';
    student_id?: string;
    assessment_date?: string;
    char_count?: number;
    subject_name?: string;
    aol?: string[];
  }
>;

type AttendanceAnalyticsEvent = AnalyticsEvent<
  | AnalyticsEventName.AttendanceRegisterListView
  | AnalyticsEventName.AttendanceRegisterFilterApplied
  | AnalyticsEventName.AttendanceRegisterExportCSV
  | AnalyticsEventName.AttendanceGroupView
  | AnalyticsEventName.AttendanceMarkStatus
  | AnalyticsEventName.AttendanceSave
  | AnalyticsEventName.AttendanceSettingsView
  | AnalyticsEventName.AttendanceCodeSave
  | AnalyticsEventName.AttendanceNotificationsToggle
  | AnalyticsEventName.AttendanceJustificationsListView
  | AnalyticsEventName.AttendanceJustificationsSearch
  | AnalyticsEventName.AttendanceJustificationView
  | AnalyticsEventName.AttendanceJustificationClosed
  | AnalyticsEventName.AttendanceJustificationReopen,
  {
    screen_name?: string;
  }
>;

type MarkbookAnalyticsEvent = AnalyticsEvent<
  AnalyticsEventName.MarkbookOpen,
  { group_name?: string }
>;

type LeavingReasonAnalyticsEvent = AnalyticsEvent<
  AnalyticsEventName.RLOpen | AnalyticsEventName.RLAddReason | AnalyticsEventName.RLArchiveReason,
  { user_role: string[] }
>;

type StudentsBulkAnalyticsEvent = AnalyticsEvent<
  AnalyticsEventName.StudentsBulkMessageParents,
  { selectionType: 'ids' | 'criteria' }
>;

type TrialRequestAnalyticsEvent = AnalyticsEvent<
  AnalyticsEventName.TrialRequestSubmission,
  { given_name: string; last_name: string; email: string }
>;

type InviteAcceptAnalyticsEvent = AnalyticsEvent<
  AnalyticsEventName.InviteAccept,
  { invite_id: string; invite_to_school_id?: string; is_trial_school?: boolean }
>;

type AnalyticsTypedEvent =
  | AnnualPlannerViewAnalyticsEvent
  | LeavingReasonAnalyticsEvent
  | MessageAnalyticsEvent
  | NotificationCenterAnalyticsEvent
  | ThreadAnalyticsEvent
  | AssessmentAnalyticsEvent
  | ReportAnalyticsEvent
  | StudentAnalyticsEvent
  | MarkbookAnalyticsEvent
  | AttendanceAnalyticsEvent
  | StudentsBulkAnalyticsEvent
  | TrialRequestAnalyticsEvent
  | InviteAcceptAnalyticsEvent;

export const trackAmplitudeEvent = ({ name, payload }: AnalyticsTypedEvent) => {
  amplitude.track(name, payload);
};

export const updateAmplitudeSessionReplayOptions = async (options: SessionReplayOptions) => {
  const sessionReplayTracking = sessionReplayPlugin(options);

  if (sessionReplayTracking.name) {
    try {
      await amplitude.remove(sessionReplayTracking.name).promise;
    } catch (error) {
      console.error('Failed to remove Amplitude Session Replay Plugin:', error);
    }
  }

  try {
    await amplitude.add(sessionReplayTracking).promise;
  } catch (error) {
    console.error('Failed to add Amplitude Session Replay Plugin:', error);
  }
};

export const setAmplitudeGroup = (groupType: string, groupName: string) => {
  amplitude.setGroup(groupType, groupName);
};

export const setAmplitudeUserProperties = (school: SyncUserSchoolRelation['school']) => {
  setAmplitudeGroup('school', school.name);

  const identifyObj = new amplitude.Identify();
  identifyObj.set('school_id', school.id);
  if (school.is_trial && school.trial_expiry_date)
    identifyObj.set('trial_expiry_date', school.trial_expiry_date);
  identifyObj.set('is_trial', school.is_trial);
  amplitude.identify(identifyObj);
};

export const startAmplitudeNewSession = () => {
  const sessionId = Date.now();
  amplitude.setSessionId(sessionId);
};

export * from './Assessments';
